import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { SESSION_ACTION_TYPE } from "../../auth/session.action";
import { MarketSelector } from "../../market/market.selector";
import { CoreState } from "../../state";
import { OpenIdConnectService } from "../open-id-connect/open-id-connect.service";

import { GOOGLE_AUTH_TRANSACTION_ACTION_TYPE, GoogleAuthTransactionAction } from "./google-auth-transaction.action";
import { OpenIdConnectAuthRequest } from "./google-auth-transaction.model";
import { GoogleAuthTransactionService } from "./google-auth-transaction.service";

@Injectable()
export class GoogleAuthTransactionEffect {
	initializeOidcAuthTransaction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GOOGLE_AUTH_TRANSACTION_ACTION_TYPE.initializeOidcAuthTransaction),
			map((x: ActionWithPayload<OpenIdConnectAuthRequest>) => x.payload),
			map((req: OpenIdConnectAuthRequest) =>
				batchActions(this.action.setOpenIdConnectUrl(), this.action.setCurrentJourney(req.provider))
			)
		)
	);

	setCurrentJourney$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(GOOGLE_AUTH_TRANSACTION_ACTION_TYPE.setCurrentJourney),
				map((x: ActionWithPayload<string>) => x.payload),
				tap((provider) => this.service.setCurrentJourney(provider))
			),
		{ dispatch: false }
	);

	setOpenIdConnectUrl$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GOOGLE_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrl),
			switchMap(() =>
				this.service.getOpenIdConnectUrl().pipe(
					map((response) => this.action.setOpenIdConnectUrlSuccess(response.endpointUri)),
					catchError((error: AppError) => of(this.action.setOpenIdConnectUrlFail(error)))
				)
			)
		)
	);

	setOpenIdConnectUrlSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(GOOGLE_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrlSuccess),
				map((x: ActionWithPayload<string>) => x.payload),
				switchMap((url) =>
					this.store
						.select(this.marketSelector.getLanguageCode())
						.pipe(map((marketCode) => ({ url, marketCode })))
				),
				map((result) =>
					this.openIdConnectService.navigateToOidcBroker(result.url, { marketCode: result.marketCode })
				),
				catchError((error: AppError) => of(this.action.setOpenIdConnectUrlFail(error)))
			),
		{ dispatch: false }
	);

	clearOnLoginWithTokenSuccessRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithTokenSuccess),
			map(() => this.action.reset())
		)
	);

	constructor(
		private action: GoogleAuthTransactionAction,
		private actions$: Actions,
		private store: Store<CoreState>,
		private service: GoogleAuthTransactionService,
		private marketSelector: MarketSelector,
		private openIdConnectService: OpenIdConnectService
	) {}
}
